import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, Renderer2 } from '@angular/core';
import { Subject, debounceTime, fromEvent, merge, takeUntil, tap, throttleTime } from 'rxjs';

@Directive({
  selector: '[appStickyContent]',
  standalone: false
})
export class StickyContentDirective implements OnDestroy, AfterViewInit {
  @Input() scrollContainerId: string = 'main';
  @Input() stickyOnlySelectors: string[] = [];

  private destroyed$: Subject<void> = new Subject();
  private static stickyElementsByContainer: Map<string, { element: HTMLElement; originalTop: number }[]> = new Map();
  private isSticky = false;
  private cachedDimensions: { width: number; left: number; height: number } | null = null;
  private static updateScheduled = false;
  private resizeObserver: ResizeObserver | null = null;

  constructor(
    private renderer: Renderer2,
    private hostElement: ElementRef
  ) {}

  ngAfterViewInit() {
    const scrollContainer = document.getElementById(this.scrollContainerId);
    if (!scrollContainer) {
      console.warn(`Scroll container with ID "${this.scrollContainerId}" not found`);
      return;
    }
    const scroll$ = fromEvent(scrollContainer, 'scroll');

    merge(scroll$.pipe(throttleTime(16)), scroll$.pipe(debounceTime(100)))
      .pipe(
        tap(() => this.setSticky(scrollContainer, this.hostElement)),
        takeUntil(this.destroyed$)
      )
      .subscribe();

    this.resizeObserver = new ResizeObserver(() => {
      if (this.isSticky) {
        const element = this.hostElement.nativeElement.firstElementChild as HTMLElement;
        if (!element) return;
        const { width, left } = this.hostElement.nativeElement.getBoundingClientRect();
        this.renderer.setStyle(element, 'width', `${width}px`);
        this.renderer.setStyle(element, 'left', `${left}px`);
      }
    });
    this.resizeObserver.observe(this.hostElement.nativeElement);
  }

  setSticky(scrollContainer: HTMLElement, hostElement: ElementRef) {
    const scrollTop = scrollContainer.scrollTop;
    const hostElementOffsetTop = hostElement.nativeElement.offsetTop;
    const stackHeight = this.calculateStackHeightBefore(hostElementOffsetTop);
    const shouldBeSticky = scrollTop + stackHeight >= hostElementOffsetTop;

    if (shouldBeSticky === this.isSticky) return;

    this.isSticky = shouldBeSticky;
    const element = hostElement.nativeElement.firstElementChild as HTMLElement;

    if (!element?.classList) return;

    if (shouldBeSticky) {
      // === BECOME STICKY ===
      if (!this.cachedDimensions) {
        const { height, width, left } = hostElement.nativeElement.getBoundingClientRect();
        this.cachedDimensions = { height, width, left };
      }
      const { width: currentWidth, left: currentLeft } = hostElement.nativeElement.getBoundingClientRect();
      const { top: scrollContainerTop } = scrollContainer.getBoundingClientRect();

      this.renderer.setStyle(hostElement.nativeElement, 'height', `${this.cachedDimensions.height}px`);

      this.renderer.addClass(element, 'stickyContainer');
      this.renderer.setStyle(element, 'top', `${scrollContainerTop + stackHeight}px`);
      this.renderer.setStyle(element, 'width', `${currentWidth}px`);
      this.renderer.setStyle(element, 'left', `${currentLeft}px`);
      this.renderer.setStyle(element, 'background-color', 'white'); // === HIDING LOGIC ===

      if (this.stickyOnlySelectors.length > 0) {
        this.renderer.addClass(element, 'sticky-selective');
        const directChildren = Array.from(element.children) as HTMLElement[];

        for (const child of directChildren) {
          const childMatches = this.stickyOnlySelectors.some((selector) => child.matches(selector));
          if (childMatches) continue;

          const descendants = Array.from(child.children) as HTMLElement[];
          let hasMatchingDescendant = false;

          if (descendants.length > 0) {
            for (const descendant of descendants) {
              const descendantMatches = this.stickyOnlySelectors.some(
                (selector) => descendant.matches(selector) // <-- !!FIXED!! Trailing comma was removed here
              );
              if (descendantMatches) {
                hasMatchingDescendant = true;
              } else {
                this.renderer.addClass(descendant, 'sticky-hidden');
              }
            }
          }
          if (!hasMatchingDescendant && !childMatches) {
            this.renderer.addClass(child, 'sticky-hidden');
          }
        }
      } // === END HIDING LOGIC ===
      if (!StickyContentDirective.stickyElementsByContainer.has(this.scrollContainerId)) {
        StickyContentDirective.stickyElementsByContainer.set(this.scrollContainerId, []);
      }
      const containerElements = StickyContentDirective.stickyElementsByContainer.get(this.scrollContainerId)!;
      if (!containerElements.find((item) => item.element === element)) {
        containerElements.push({ element, originalTop: hostElementOffsetTop });
        containerElements.sort((a, b) => a.originalTop - b.originalTop);
        this.scheduleLastStickyUpdate();
      }
    } else {
      // === BECOME UN-STICKY ===
      this.cachedDimensions = null;
      this.renderer.setStyle(hostElement.nativeElement, 'height', 'unset');

      this.renderer.removeClass(element, 'stickyContainer');
      this.renderer.removeClass(element, 'stickyContainer-last');
      this.renderer.setStyle(element, 'top', 'unset');
      this.renderer.setStyle(element, 'width', 'unset');
      this.renderer.setStyle(element, 'left', 'unset');
      this.renderer.setStyle(element, 'background-color', 'unset'); // === UN-HIDING LOGIC ===

      if (this.stickyOnlySelectors.length > 0) {
        this.renderer.removeClass(element, 'sticky-selective');
        const hiddenElements = element.querySelectorAll('.sticky-hidden');
        hiddenElements.forEach((el) => this.renderer.removeClass(el, 'sticky-hidden'));
      } // === END UN-HIDING LOGIC ===
      const containerElements = StickyContentDirective.stickyElementsByContainer.get(this.scrollContainerId);
      if (containerElements) {
        const index = containerElements.findIndex((item) => item.element === element);
        if (index > -1) {
          containerElements.splice(index, 1);
          this.scheduleLastStickyUpdate();
        }
      }
    }
  }

  private calculateStackHeightBefore(currentElementTop: number): number {
    const containerElements = StickyContentDirective.stickyElementsByContainer.get(this.scrollContainerId) || [];
    let height = 0;
    for (const item of containerElements) {
      if (item.originalTop < currentElementTop && item.element.classList.contains('stickyContainer')) {
        height += item.element.offsetHeight;
      }
    }
    return height;
  }

  private scheduleLastStickyUpdate(): void {
    if (StickyContentDirective.updateScheduled) return;
    StickyContentDirective.updateScheduled = true;
    requestAnimationFrame(() => {
      this.updateLastStickyClass();
      StickyContentDirective.updateScheduled = false;
    });
  }

  private updateLastStickyClass(): void {
    const containerElements = StickyContentDirective.stickyElementsByContainer.get(this.scrollContainerId) || [];
    const stickyElements = containerElements.filter((item) => item.element.classList.contains('stickyContainer'));

    stickyElements.forEach((item) => this.renderer.removeClass(item.element, 'stickyContainer-last'));

    if (stickyElements.length > 0) {
      const lastElement = stickyElements[stickyElements.length - 1];
      this.renderer.addClass(lastElement.element, 'stickyContainer-last');
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.resizeObserver?.disconnect();

    const containerElements = StickyContentDirective.stickyElementsByContainer.get(this.scrollContainerId);
    if (containerElements) {
      const element = this.hostElement.nativeElement.firstElementChild as HTMLElement;
      if (element) {
        const index = containerElements.findIndex((item) => item.element === element);
        if (index > -1) {
          containerElements.splice(index, 1);
        }
        if (containerElements.length === 0) {
          StickyContentDirective.stickyElementsByContainer.delete(this.scrollContainerId);
        }
      }
    }
  }
}
